import {
  HasAccess,
  useIsSuperAdmin,
} from "@/components/common/has-access";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Role, roleDisplayMap, roles } from "@/helpers/role-permission";
import { InternalUser } from "@/pages/internal-user/types/internal-user";
import { MoreHorizontal, Pencil, X } from "lucide-react";
import { useState } from "react";
import {
  useRemoveInternalUser,
  useUpdateExotelPhone,
  useUpdateInternalUserRole,
  useUpdateOtherExotelPhone,
} from "../hooks/use-internal-user";

interface InternalUserCardProps {
  internalUser: InternalUser;
  isCurrentUser: Boolean;
}

export const InternalUserCard: React.FC<InternalUserCardProps> = ({
  internalUser,
  isCurrentUser,
}) => {
  const { first_name, last_name, email, image_url, role, id, clerk_id } =
    internalUser;
  const [showBanConfirm, setShowBanConfirm] = useState(false);
  const [editingExotel, setEditingExotel] = useState(false);
  const [exotelPhone, setExotelPhone] = useState(
    internalUser.exotel_phone_number ?? ""
  );
  // Only a super role may edit another person's Exotel number. The server's
  // answer, not Clerk metadata — the same store RolesGuard enforces.
  const { isSuperAdmin: showExotelEdit } = useIsSuperAdmin();

  const { mutate: updateRole } = useUpdateInternalUserRole();
  const { mutate: deleteInternalUser } = useRemoveInternalUser();
  const { mutate: saveMyExotelPhone, isPending: savingMyPhone } =
    useUpdateExotelPhone();
  const { mutate: saveOtherExotelPhone, isPending: savingOtherPhone } =
    useUpdateOtherExotelPhone();
  const savingPhone = savingMyPhone || savingOtherPhone;

  const handleExotelSave = () => {
    const done = () => setEditingExotel(false);
    if (isCurrentUser) {
      saveMyExotelPhone(exotelPhone || null, { onSuccess: done });
    } else {
      saveOtherExotelPhone(
        { target_clerk_id: clerk_id, phone: exotelPhone || null },
        { onSuccess: done },
      );
    }
  };

  const handleExotelCancel = () => {
    setExotelPhone(internalUser.exotel_phone_number ?? "");
    setEditingExotel(false);
  };

  const renderRoleBadge = () => {
    if (!roleDisplayMap[role.name as Role]) return null;

    const displayName = roleDisplayMap[internalUser.role.name as Role];
    const variant =
      internalUser.role.name === "super_admin" ? "completed" : "outline";

    return (
      <Badge className="ml-2" variant={variant}>
        {displayName}
      </Badge>
    );
  };

  // Only the role is sent. The server derives the permissions from the role's
  // rows in the database — a grant computed here would be ignored (RH-888).
  const handleRoleChange = (newRole: Role) => {
    updateRole({ clerk_id: clerk_id, role: newRole });
  };

  return (
    <Card
      key={id}
      className={`w-full ${
        isCurrentUser ? "border-primary border-1 shadow-lg elevation-2" : ""
      }`}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 bg-secondary-background">
        <div>
          <CardTitle className="text-xl font-bold text-sidebar-primary">
            <>
              {first_name && last_name
                ? `${first_name} ${last_name}`
                : first_name || last_name
                ? first_name || last_name
                : "No Name"}
            </>
            {isCurrentUser && (
              <Badge variant="outline" className="ml-2">
                You
              </Badge>
            )}
          </CardTitle>
        </div>
        <div className="flex items-center mb-1">
          {renderRoleBadge()}{" "}
          <HasAccess permission="manage_admins">
            <>
              <Dialog open={showBanConfirm} onOpenChange={setShowBanConfirm}>
                <DialogTrigger asChild>
                  <Button variant="destructive" size="sm" className="ml-2">
                    Remove User
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Confirm Removal</DialogTitle>
                    <DialogDescription>
                      Are you sure you want to remove{" "}
                      {first_name || "this user"} ? This action cannot be
                      undone.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button
                      variant="ghost"
                      onClick={() => setShowBanConfirm(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => {
                        deleteInternalUser(internalUser.clerk_id);
                        setShowBanConfirm(false);
                      }}
                    >
                      Confirm Removal
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          </HasAccess>
        </div>
      </CardHeader>
      <CardContent className="flex items-center justify-between p-4 w-full bg-secondary-background">
        <div className="flex items-center">
          <img
            src={image_url}
            alt={first_name || "No name"}
            className="h-12 w-12 rounded-full mr-4"
          />
          <div>
            <p className="font-medium text-sm text-muted-foreground">{email}</p>
          </div>
        </div>
        {showExotelEdit && (
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted-foreground">ExoTel:</span>
            {editingExotel ? (
              <>
                <Input
                  value={exotelPhone}
                  placeholder="e.g. 08238487926"
                  onChange={(e) => setExotelPhone(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleExotelSave();
                    if (e.key === "Escape") handleExotelCancel();
                  }}
                  className="h-6 text-xs w-36 px-2"
                  autoFocus
                />
                <Button
                  size="sm"
                  className="h-6 text-xs px-2"
                  disabled={savingPhone}
                  onClick={handleExotelSave}
                >
                  Save
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={handleExotelCancel}
                >
                  <X className="h-3 w-3" />
                </Button>
              </>
            ) : (
              <>
                <span className="text-xs text-gray-700">
                  {internalUser.exotel_phone_number || "—"}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5"
                  onClick={() => setEditingExotel(true)}
                >
                  <Pencil className="h-3 w-3" />
                </Button>
              </>
            )}
          </div>
        )}
      </CardContent>
      <CardFooter className="justify-end">
        <HasAccess permission="manage_admins">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">Actions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {roles.map((r) => (
                <DropdownMenuItem
                  key={r}
                  onSelect={() => handleRoleChange(r)}
                  disabled={r === internalUser.role.name}
                >
                  {roleDisplayMap[r]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </HasAccess>
      </CardFooter>
    </Card>
  );
};
